import { Crown, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Renders a string containing <b>...</b> tags as React elements with <strong>. */
export function renderBoldText(text: string): React.ReactNode {
  const parts = text.split(/(<b>.*?<\/b>)/g);
  return parts.map((part, i) => {
    const match = part.match(/^<b>(.*?)<\/b>$/);
    if (match) {
      return <strong key={i}>{match[1]}</strong>;
    }
    return part;
  });
}

export function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export function useCountUp(end: number, duration = 1800, start = 0, active = false) {
  const [val, setVal] = useState(start);
  useEffect(() => {
    if (!active) return undefined;
    let raf: number;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      setVal(Math.round(start + (end - start) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, end, duration, start]);
  return val;
}

export const sectionCls = (visible: boolean) =>
  `transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

/* Mini preview: Dashboard */
export const MiniDashboardPreview = () => (
  <div className="w-full aspect-[4/3] rounded-xl bg-gradient-to-br from-muted/80 via-muted/50 to-primary/5 border border-border/30 p-3 overflow-hidden">
    <div className="h-full rounded-lg bg-background border border-border/20 p-2.5 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] font-semibold text-foreground">Tonight's Pulse</div>
        <div className="flex items-center gap-1 text-[6px] text-success">
          <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          Live
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        <div className="rounded-md bg-muted/40 p-1.5">
          <div className="text-[6px] text-muted-foreground">Revenue</div>
          <div className="text-[10px] font-bold text-foreground">$24.8K</div>
          <div className="text-[5px] text-success">^ 18%</div>
        </div>
        <div className="rounded-md bg-muted/40 p-1.5">
          <div className="text-[6px] text-muted-foreground">Tickets</div>
          <div className="text-[10px] font-bold text-foreground">347</div>
          <div className="text-[5px] text-primary">82% cap</div>
        </div>
        <div className="rounded-md bg-muted/40 p-1.5">
          <div className="text-[6px] text-muted-foreground">VIPs</div>
          <div className="text-[10px] font-bold text-foreground">12</div>
          <div className="text-[5px] text-accent">$48K LTV</div>
        </div>
      </div>
      <div className="flex-1 rounded-md bg-muted/30 p-1.5 flex items-start gap-1.5">
        <Sparkles className="h-2.5 w-2.5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-[6px] text-muted-foreground leading-relaxed">
          Ticket velocity 23% above avg. Consider adding 2 bartenders to main bar.
        </div>
      </div>
    </div>
  </div>
);

/* Mini preview: Hyped Artist */
export const MiniHypedPreview = () => (
  <div className="w-full aspect-[4/3] rounded-xl bg-gradient-to-br from-muted/80 via-muted/50 to-amber-500/5 border border-border/30 p-3 overflow-hidden">
    <div className="h-full rounded-lg bg-background border border-border/20 p-2.5 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] font-semibold text-foreground">Artist Discovery</div>
        <div className="text-[6px] text-muted-foreground">Hype Scores</div>
      </div>
      {[
        {
          name: 'Kaytranada',
          genre: 'Electronic',
          score: 94,
          trend: '+12%',
        },
        { name: 'Peggy Gou', genre: 'House', score: 89, trend: '+8%' },
        {
          name: 'Fred again..',
          genre: 'Electronic',
          score: 86,
          trend: '+15%',
        },
      ].map((a, i) => (
        <div
          key={a.name}
          className={`flex items-center gap-2 p-1.5 rounded-md ${i === 0 ? 'bg-amber-500/10' : 'bg-muted/30'} mb-1`}
        >
          <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[7px] font-bold text-foreground">
            {i + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[7px] font-semibold text-foreground">{a.name}</div>
            <div className="text-[5px] text-muted-foreground">{a.genre}</div>
          </div>
          <div className="text-right">
            <div className="text-[8px] font-bold text-amber-600">{a.score}</div>
            <div className="text-[5px] text-success">{a.trend}</div>
          </div>
        </div>
      ))}
      <div className="mt-auto pt-1.5 border-t border-border/30 text-[5px] text-muted-foreground">
        Analyzing 2,400+ artists in your market
      </div>
    </div>
  </div>
);

/* Mini preview: VIP */
export const MiniVIPPreview = () => (
  <div className="w-full aspect-[4/3] rounded-xl bg-gradient-to-br from-muted/80 via-muted/50 to-amber-500/5 border border-border/30 p-3 overflow-hidden">
    <div className="h-full rounded-lg bg-background border border-border/20 p-2.5 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Crown className="h-3 w-3 text-amber-500" />
          <div className="text-[9px] font-semibold text-foreground">VIP Intelligence</div>
        </div>
        <div className="text-[6px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
          3 alerts
        </div>
      </div>
      <div className="rounded-md bg-amber-500/8 border border-amber-500/20 p-2 mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-[7px] font-bold text-amber-600">
            JR
          </div>
          <div className="flex-1">
            <div className="text-[7px] font-semibold text-foreground">James Rodriguez</div>
            <div className="text-[5px] text-muted-foreground">
              $12.4K lifetime - Last visit 3 weeks ago
            </div>
          </div>
          <div className="text-[5px] text-amber-600 font-medium">Whale</div>
        </div>
      </div>
      {[
        { init: 'SK', name: 'Sarah K.', ltv: '$8.2K', tag: 'VIP' },
        { init: 'ML', name: 'Mike L.', ltv: '$6.1K', tag: 'At-risk' },
      ].map((v) => (
        <div key={v.init} className="flex items-center gap-2 p-1.5 rounded-md bg-muted/30 mb-1">
          <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[6px] font-bold text-muted-foreground">
            {v.init}
          </div>
          <div className="flex-1">
            <div className="text-[6px] font-medium text-foreground">{v.name}</div>
            <div className="text-[5px] text-muted-foreground">{v.ltv} lifetime</div>
          </div>
          <div
            className={`text-[5px] px-1 py-0.5 rounded ${v.tag === 'At-risk' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}
          >
            {v.tag}
          </div>
        </div>
      ))}
      <div className="mt-auto pt-1.5 border-t border-border/30 text-[5px] text-muted-foreground">
        Auto-flagged from ticket + POS data
      </div>
    </div>
  </div>
);
